import { environment } from '@/helpers/Environment';
import { logInTrackJS } from '@/errors/helpers';
/**
 * Navigate back. Go Home if there's no history.
 */
export function navigateBack(router) {
    let goneBack = false;
    // Arbitrary number to make sure that the check happens after `popstate`
    // callback is fired
    const goneBackCheckDelay = 300;
    const popstateCallback = () => (goneBack = true);
    window.addEventListener('popstate', popstateCallback);
    router.go(-1);
    setTimeout(() => {
        if (!goneBack) {
            router.push('/');
        }
        window.removeEventListener('popstate', popstateCallback);
        goneBack = false;
    }, goneBackCheckDelay);
}
export function fullPathMinusHash(fullPath) {
    return fullPath.split('#')[0];
}
/**
 * Check whether a path stays on the current origin when resolved.
 * Used to prevent open-redirect attacks on user-supplied redirect paths.
 *
 * Uses the browser's WHATWG URL parser — same parser for assigning to `window.location.href`.
 */
export function isSameOriginPath(path) {
    try {
        const origin = window.location.origin;
        if (new URL(path, origin).origin !== origin) {
            return false;
        }
        // Prepend can change origin (`\evil.com` → `/\evil.com` → `//evil.com`).
        if (!path.startsWith('/')) {
            return new URL(`/${path}`, origin).origin === origin;
        }
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Parse and validate a `redirect` query parameter.
 * Returns a safe, same-origin path or `null` if the value is missing/invalid.
 */
export function parseRedirectParam(param) {
    if (typeof param !== 'string') {
        return null;
    }
    if (!isSameOriginPath(param)) {
        logInTrackJS('parseRedirectParam: Invalid redirect URL', { redirect: param });
        return null;
    }
    return param.startsWith('/') ? param : `/${param}`;
}
/**
 * Prepend the seller slug to a path when the app runs under a seller prefix.
 * E.g. `/events/123` → `/seller1/events/123`.
 *
 * Idempotent when the path already starts with the current seller slug (including seller-root `?` and `#` forms). Safe to call on paths that may already be prefixed — e.g. login/logout `redirect` params from another app.
 *
 * Use this whenever you assign to `window.location.href` with an app-relative path — Vue Router's `base` option handles this automatically for `router.push`, but raw location assignments do not.
 */
export function withSellerPrefix(path) {
    const slug = environment.seller?.slug;
    // Remove leading slash to avoid double slashes when concatenating with slug
    const normalizedPath = path.replace(/^\//, '');
    if (!slug)
        return `/${normalizedPath}`;
    if (normalizedPath === slug ||
        normalizedPath.startsWith(`${slug}/`) ||
        normalizedPath.startsWith(`${slug}?`) ||
        normalizedPath.startsWith(`${slug}#`)) {
        return `/${normalizedPath}`;
    }
    return `/${slug}/${normalizedPath}`;
}
export function openEcommNext(path, method = 'assign') {
    const withSeller = withSellerPrefix(path);
    if (method === 'replace')
        window.location.replace(withSeller);
    else
        window.location.assign(withSeller);
}
